// Class strings copied from the pages around this one (PerfectPaymentMethodsPage,
// NotificationsPage) so the broadcast screens match the rest of the admin
// without introducing a component library the app does not have.

export const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

export const btnBase = "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 gap-2";

export const btnPrimary = `${btnBase} bg-primary text-primary-foreground hover:bg-primary/90`;
export const btnGhost = `${btnBase} border border-input bg-background hover:bg-secondary`;
export const btnDanger = `${btnBase} border border-input bg-background text-destructive hover:bg-destructive/10`;

export const iconBtn = "p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors";

export const th = "h-10 px-4 align-middle font-medium text-muted-foreground";

export function statusPill(tone) {
    const tones = {
        live: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        off: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        ended: 'bg-muted text-muted-foreground',
        neutral: 'bg-muted text-muted-foreground'
    };
    return `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tones[tone] || tones.neutral}`;
}

/**
 * API instant -> the value <input type="datetime-local"> expects.
 * ISO values carry their zone, so they are converted into the viewer's local
 * time. A bare DATETIME (older rows) has no zone and is taken as-is.
 */
export function toLocalInput(value) {
    if (!value) return '';
    const raw = String(value);
    if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
        const date = new Date(raw);
        if (!isNaN(date.getTime())) return nowLocalInput(date);
    }
    if (raw.includes('T')) return raw.slice(0, 16);
    return raw.replace(' ', 'T').slice(0, 16);
}

/**
 * A datetime-local value (the operator's wall clock) -> an absolute ISO instant.
 * The server stores server-local time, so sending an absolute instant is what
 * keeps a start time picked in one timezone from drifting in another.
 */
export function toIsoInstant(value) {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString();
}

/** Panel-less broadcast target selector value. */
export const ALL_PANELS = '';

export function scheduleState(row, now = Date.now()) {
    if (!row.is_active) return { label: 'Off', tone: 'off' };
    const start = row.starts_at ? new Date(row.starts_at).getTime() : null;
    const end = row.ends_at ? new Date(row.ends_at).getTime() : null;
    if (start && start > now) return { label: 'Scheduled', tone: 'scheduled' };
    if (end && end < now) return { label: 'Ended', tone: 'ended' };
    return { label: 'Live', tone: 'live' };
}

/** The current local minute, in the shape <input type="datetime-local"> wants. */
export function nowLocalInput(date = new Date()) {
    const p = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
        + `T${p(date.getHours())}:${p(date.getMinutes())}`;
}

/** Shift a datetime-local value by whole days, staying in the same shape. */
export function addDaysLocalInput(value, days) {
    const date = value ? new Date(value) : new Date();
    if (isNaN(date.getTime())) return '';
    date.setDate(date.getDate() + days);
    return nowLocalInput(date);
}

export const EXPIRY_OPTIONS = [
    { value: '', label: 'No expiry' },
    { value: '1', label: '1 day' },
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' }
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Map a stored window back onto the expiry selector. Anything that does not land
 * on a preset (an older row, or one written through the API) becomes 'custom' so
 * editing another field cannot silently move its end date.
 */
export function expiryFor(startsAt, endsAt) {
    if (!endsAt) return { expiry: '', ends_at: '' };

    const end = new Date(endsAt).getTime();
    const start = startsAt ? new Date(startsAt).getTime() : NaN;
    if (!isNaN(start) && !isNaN(end)) {
        const days = Math.round((end - start) / DAY_MS);
        if (days === 1 || days === 7 || days === 30) {
            return { expiry: String(days), ends_at: '' };
        }
    }
    return { expiry: 'custom', ends_at: toLocalInput(endsAt) };
}

/** Human-readable end date for the 'custom' option label. */
export function formatEnds(value) {
    if (!value) return 'current end date';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'current end date';
    return date.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
}
